import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Battery, Heart, TrendingUp, Activity, Brain } from 'lucide-react';
import { WhoopDayData } from '@/hooks/useWhoopData';
import { AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { format } from 'date-fns';
import WhoopHistoryChart from './WhoopHistoryChart';
import WhoopMetricCard from './WhoopMetricCard';
import { TimeRange } from './WhoopTimeRangeSelector';

interface WhoopRecoveryDetailProps {
  data: WhoopDayData[];
  selectedData: WhoopDayData | null;
  timeRange: TimeRange;
}

export default function WhoopRecoveryDetail({ data, selectedData, timeRange }: WhoopRecoveryDetailProps) {
  const getRecoveryColor = (score?: number | null) => {
    if (!score) return 'text-muted-foreground';
    if (score >= 67) return 'text-accent';
    if (score >= 34) return 'text-primary';
    return 'text-secondary';
  };

  const getRecoveryStatus = (score?: number | null) => {
    if (!score) return 'No Data';
    if (score >= 67) return 'Optimal Recovery';
    if (score >= 34) return 'Moderate Recovery';
    return 'Low Recovery';
  };

  const getRecoveryBgClass = (score?: number | null) => {
    if (!score) return 'bg-muted/20';
    if (score >= 67) return 'bg-accent/10 border-accent/30';
    if (score >= 34) return 'bg-primary/10 border-primary/30';
    return 'bg-secondary/10 border-secondary/30';
  };

  // Calculate stats
  const validRecovery = data.filter(d => d.recovery_score !== null);
  const avgRecovery = validRecovery.length > 0 
    ? validRecovery.reduce((sum, d) => sum + (d.recovery_score || 0), 0) / validRecovery.length 
    : 0;
  
  const validHrv = data.filter(d => d.hrv_score !== null);
  const avgHrv = validHrv.length > 0 
    ? validHrv.reduce((sum, d) => sum + (d.hrv_score || 0), 0) / validHrv.length 
    : 0;

  const validRhr = data.filter(d => d.resting_heart_rate !== null);
  const avgRhr = validRhr.length > 0 
    ? validRhr.reduce((sum, d) => sum + (d.resting_heart_rate || 0), 0) / validRhr.length 
    : 0;

  // Calculate trend (compare first half vs second half)
  const midPoint = Math.floor(validRecovery.length / 2);
  const recentAvg = midPoint > 0 
    ? validRecovery.slice(0, midPoint).reduce((sum, d) => sum + (d.recovery_score || 0), 0) / midPoint 
    : avgRecovery;
  const olderAvg = midPoint > 0 
    ? validRecovery.slice(midPoint).reduce((sum, d) => sum + (d.recovery_score || 0), 0) / (validRecovery.length - midPoint) 
    : avgRecovery;
  const trendDiff = recentAvg - olderAvg;
  const trend: 'up' | 'down' | 'stable' = trendDiff > 3 ? 'up' : trendDiff < -3 ? 'down' : 'stable';

  // Prepare chart data
  const chartData = [...data].reverse().map(d => ({
    date: format(new Date(d.date), timeRange === 'month' ? 'MMM d' : 'EEE'),
    fullDate: format(new Date(d.date), 'EEE, MMM d'),
    recovery: d.recovery_score,
    hrv: d.hrv_score,
    rhr: d.resting_heart_rate
  }));

  // Recovery zone distribution
  const zoneData = [
    { zone: 'Low (0-33%)', count: validRecovery.filter(d => (d.recovery_score || 0) < 34).length, fill: 'hsl(var(--secondary))' },
    { zone: 'Moderate (34-66%)', count: validRecovery.filter(d => (d.recovery_score || 0) >= 34 && (d.recovery_score || 0) < 67).length, fill: 'hsl(var(--primary))' },
    { zone: 'Optimal (67-100%)', count: validRecovery.filter(d => (d.recovery_score || 0) >= 67).length, fill: 'hsl(var(--accent))' },
  ];

  // For period views, calculate summary values
  const periodLabel = timeRange === 'day' 
    ? (selectedData ? format(new Date(selectedData.date), 'EEEE, MMMM d') : 'Today')
    : timeRange === 'week' 
      ? '7-Day Average' 
      : '30-Day Average';

  const displayRecovery = timeRange === 'day' 
    ? selectedData?.recovery_score 
    : avgRecovery;
  const displayHrv = timeRange === 'day' 
    ? selectedData?.hrv_score 
    : avgHrv;
  const displayRhr = timeRange === 'day' 
    ? selectedData?.resting_heart_rate 
    : avgRhr;

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      {/* Main Summary - Shows single day or averages based on timeRange */}
      {(selectedData || data.length > 0) && (
        <Card className={`border-2 ${getRecoveryBgClass(displayRecovery)}`}>
          <CardHeader className="pb-2 sm:pb-4">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Battery className="h-4 w-4 sm:h-5 sm:w-5 text-accent" />
              {timeRange === 'day' ? 'Recovery Score' : 'Average Recovery'}
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              {periodLabel}
              {timeRange !== 'day' && ` • ${data.length} days of data`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 sm:space-y-6">
            <div className="text-center py-2 sm:py-4">
              <div className={`text-5xl sm:text-7xl font-bold ${getRecoveryColor(displayRecovery)}`}>
                {displayRecovery ? Math.round(displayRecovery) : '--'}%
              </div>
              <p className={`text-base sm:text-lg font-medium mt-1 sm:mt-2 ${getRecoveryColor(displayRecovery)}`}>
                {getRecoveryStatus(displayRecovery)}
              </p>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              <WhoopMetricCard
                icon={Heart}
                label={timeRange === 'day' ? 'HRV' : 'Avg HRV'}
                value={displayHrv ? Math.round(displayHrv) : '--'}
                unit="ms"
                colorClass="text-accent"
                bgClass="bg-accent/5"
              />
              <WhoopMetricCard
                icon={Heart}
                label={timeRange === 'day' ? 'Resting HR' : 'Avg Resting HR'}
                value={displayRhr ? Math.round(displayRhr) : '--'}
                unit="bpm"
                colorClass="text-secondary"
                bgClass="bg-secondary/5"
              />
              <WhoopMetricCard
                icon={Activity}
                label={timeRange === 'day' ? 'Readiness' : 'Avg Readiness'}
                value={displayRecovery && displayHrv 
                  ? Math.round((displayRecovery + displayHrv) / 2) 
                  : '--'}
                colorClass="text-primary"
                bgClass="bg-primary/5"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Period Overview Cards - Only show for week/month with multiple days */}
      {timeRange !== 'day' && data.length > 1 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
          <WhoopMetricCard
            icon={Battery}
            label={`Avg Recovery (${timeRange === 'week' ? '7d' : '30d'})`}
            value={avgRecovery.toFixed(0)}
            unit="%"
            trend={trend}
            trendValue={`${trendDiff > 0 ? '+' : ''}${trendDiff.toFixed(0)}%`}
            colorClass={getRecoveryColor(avgRecovery)}
            bgClass={getRecoveryBgClass(avgRecovery)}
          />
          <WhoopMetricCard
            icon={TrendingUp}
            label="Avg HRV"
            value={avgHrv.toFixed(0)}
            unit="ms"
            colorClass="text-accent"
            bgClass="bg-accent/5"
          />
          <WhoopMetricCard
            icon={Heart}
            label="Avg Resting HR"
            value={avgRhr.toFixed(0)}
            unit="bpm"
            colorClass="text-secondary"
            bgClass="bg-secondary/5"
          />
          <WhoopMetricCard
            icon={Activity}
            label="Days Tracked"
            value={validRecovery.length}
            colorClass="text-primary"
            bgClass="bg-primary/5"
          />
        </div>
      )}

      {/* Recovery Trend Chart */}
      <Card>
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="text-base sm:text-lg">Recovery Trend</CardTitle>
          <CardDescription className="text-xs sm:text-sm">Track your recovery patterns over time</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220} className="sm:!h-[300px]">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="recoveryGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
                formatter={(value: number) => [`${Math.round(value)}%`, 'Recovery']}
                labelFormatter={(label, payload) => payload[0]?.payload?.fullDate || label}
              />
              <ReferenceLine y={avgRecovery} stroke="hsl(var(--primary))" strokeDasharray="5 5" />
              <Area
                type="monotone"
                dataKey="recovery"
                stroke="hsl(var(--accent))"
                strokeWidth={2}
                fill="url(#recoveryGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* HRV & Resting HR Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">HRV & Resting Heart Rate</CardTitle>
          <CardDescription>Your autonomic nervous system balance indicators</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" stroke="hsl(var(--accent))" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" stroke="hsl(var(--secondary))" tick={{ fontSize: 11 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
                labelFormatter={(label, payload) => payload[0]?.payload?.fullDate || label}
              />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="hrv" stroke="hsl(var(--accent))" strokeWidth={2} name="HRV (ms)" dot={{ r: 3 }} />
              <Line yAxisId="right" type="monotone" dataKey="rhr" stroke="hsl(var(--secondary))" strokeWidth={2} name="RHR (bpm)" dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Recovery Zone Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recovery Zone Distribution</CardTitle>
          <CardDescription>How your recovery scores are distributed</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {zoneData.map((zone) => (
              <div key={zone.zone} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{zone.zone}</span>
                  <span className="text-muted-foreground">{zone.count} days ({validRecovery.length > 0 ? Math.round((zone.count / validRecovery.length) * 100) : 0}%)</span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: validRecovery.length > 0 ? `${(zone.count / validRecovery.length) * 100}%` : '0%',
                      backgroundColor: zone.fill
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Psychological Insights */}
      <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-primary/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            Recovery & Mental Wellbeing
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-foreground/90 leading-relaxed">
            Your recovery score reflects your nervous system's ability to restore and regulate. Higher HRV indicates better parasympathetic (rest & digest) activity, while lower resting heart rate suggests cardiovascular efficiency.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-3 bg-accent/10 rounded-lg border border-accent/30">
              <p className="text-sm font-medium text-accent">Optimal (67%+)</p>
              <p className="text-xs text-muted-foreground mt-1">Ready for challenging activities, therapy work, and emotional processing</p>
            </div>
            <div className="p-3 bg-primary/10 rounded-lg border border-primary/30">
              <p className="text-sm font-medium text-primary">Moderate (34-66%)</p>
              <p className="text-xs text-muted-foreground mt-1">Balance activity with rest; gentle regulation practices recommended</p>
            </div>
            <div className="p-3 bg-secondary/10 rounded-lg border border-secondary/30">
              <p className="text-sm font-medium text-secondary">Low (0-33%)</p>
              <p className="text-xs text-muted-foreground mt-1">Prioritise rest, safety, and co-regulation activities</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
